import {
  ToggleButton,
  ToggleButtonGroup,
  Box,
  TextField,
  Autocomplete,
  Checkbox,
  Chip,
} from "@mui/material";
import React, { useState, useEffect } from "react";
import { grey } from "@mui/material/colors";
import dayjs from "dayjs";
import Select from "components/Select";
import LightModeIcon from "@mui/icons-material/LightMode";
import WindPowerIcon from "@mui/icons-material/WindPower";
import ForestIcon from "@mui/icons-material/Forest";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";

const generateMonths = (monthsBack: number, monthsAhead: number) => {
  const months = [];
  const start = dayjs().subtract(monthsBack, "month");

  const totalMonths = monthsBack + monthsAhead;

  for (let i = 0; i <= totalMonths; i++) {
    months.push(start.add(i, "month").format("MM-YYYY"));
  }

  return months;
};

const generateFutureMonths = (monthsAhead: number) => {
  const months = [];
  for (let i = 0; i < monthsAhead; i++) {
    months.push(dayjs().add(i, "month").format("MM-YYYY"));
  }
  return months;
};

const convertMonthToLastDay = (monthYearStr: string) => {
  const [month, year] = monthYearStr.split("-").map(Number);
  return dayjs(`${year}-${String(month).padStart(2, "0")}-01`)
    .endOf("month")
    .format("YYYY-MM-DD");
};

const convertLastDayToMonth = (dateStr: string) => {
  return dayjs(dateStr).format("MM-YYYY");
};

const getStartAndEndUTC = (monthYearStr: string) => {
  const [month, year] = monthYearStr.split("-").map(Number);

  const start = dayjs
    .tz(`${year}-${String(month - 1).padStart(2, "0")}-01`, "Europe/Tallinn")
    .endOf("month")
    .utc()
    .format()
    .replace("Z", "");

  const end = dayjs
    .tz(`${year}-${String(month).padStart(2, "0")}-01`, "Europe/Tallinn")
    .endOf("month")
    .utc()
    .format()
    .replace("Z", "");

  return { start, end };
};

const productionPeriodToMonth = (period: string) =>
  dayjs
    .utc(period.split("/")[0])
    .tz("Europe/Tallinn")
    .add(1, "second")
    .format("MM-YYYY");

const Filters = ({ filters, setFilters }) => {
  const [clientName, setClientName] = useState(filters.name || "");

  useEffect(() => {
    setClientName(filters.name || "");
  }, [filters.name]);

  useEffect(() => {
    const debounceTimeout = setTimeout(() => {
      setFilters((prev) => ({ ...prev, name: clientName }));
    }, 500);

    return () => clearTimeout(debounceTimeout);
  }, [clientName]);

  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="start"
      alignItems="start"
      gap="1rem"
      sx={{
        background: grey[100],
        borderRadius: "4px",
        padding: "1.4rem",
        maxWidth: "100%",
      }}
    >
      <Box display="flex" justifyContent="start" alignItems="center">
        <TextField
          size="small"
          variant="outlined"
          label="Client"
          sx={{ background: "white", mr: 2 }}
          value={clientName}
          onChange={(e) => setClientName(e.target.value)}
        />
        <ToggleButtonGroup
          value={filters.technologyCode}
          size="small"
          exclusive
          onChange={(e, value) => {
            setFilters(() => ({ ...filters, technologyCode: value }));
          }}
          aria-label="fuel type"
          sx={{ background: "white" }}
        >
          <ToggleButton value="T010000" aria-label="solar">
            <LightModeIcon size="small" />
          </ToggleButton>
          <ToggleButton value="wind" aria-label="wind">
            <WindPowerIcon />
          </ToggleButton>
          <ToggleButton value="T050202" aria-label="biomass">
            <ForestIcon />
          </ToggleButton>
          <ToggleButton value="other" aria-label="other">
            <MoreHorizIcon />
          </ToggleButton>
        </ToggleButtonGroup>
        <ToggleButtonGroup
          value={filters.country}
          size="small"
          exclusive
          onChange={(e) =>
            setFilters(() => ({ ...filters, country: e.target.value }))
          }
          aria-label="country"
          sx={{ ml: 2, background: "white" }}
        >
          <ToggleButton value="EE" aria-label="EE" sx={{ p: "0.5rem 0.8rem" }}>
            EE
          </ToggleButton>
          <ToggleButton value="LV" aria-label="LV" sx={{ p: "0.5rem 0.8rem" }}>
            LV
          </ToggleButton>
          <ToggleButton value="LT" aria-label="LT" sx={{ p: "0.5rem 0.8rem" }}>
            LT
          </ToggleButton>
        </ToggleButtonGroup>
        <Box sx={{ ml: 2 }}>
          <Select
            label="GO valid until"
            id="expiryDate"
            name="expiryDate"
            value={
              filters.expiryDate
                ? convertLastDayToMonth(filters.expiryDate)
                : "all"
            }
            onChange={(e) =>
              setFilters(() => ({
                ...filters,
                expiryDate:
                  e.target.value === "all"
                    ? null
                    : convertMonthToLastDay(e.target.value),
              }))
            }
            minWidth={200}
            options={[
              { label: "All", value: "all" },
              ...generateMonths(0, 18).map((month) => ({
                label: month,
                value: month,
              })),
            ]}
          />
        </Box>
        <Box sx={{ ml: 2, minWidth: 260, maxWidth: 360 }}>
          <Autocomplete
            multiple
            id="productionPeriod"
            size="small"
            disableCloseOnSelect
            options={generateMonths(12, 0).reverse()}
            value={(filters.productionPeriod || []).map(productionPeriodToMonth)}
            onChange={(_e, selectedMonths: string[]) => {
              const productionPeriod = selectedMonths.map((m) => {
                const { start, end } = getStartAndEndUTC(m);
                return `${start}/${end}`;
              });
              setFilters(() => ({
                ...filters,
                productionPeriod,
              }));
            }}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => {
                const { key, ...tagProps } = getTagProps({ index });
                return (
                  <Chip
                    {...tagProps}
                    key={key}
                    label={option}
                    size="small"
                  />
                );
              })
            }
            renderOption={(props, option, { selected }) => (
              <li {...props} style={{ padding: "0 0.5rem" }}>
                <Checkbox
                  icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                  checkedIcon={<CheckBoxIcon fontSize="small" />}
                  style={{ marginRight: 4 }}
                  checked={selected}
                />
                {option}
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Production period"
                size="small"
                sx={{ background: "white" }}
              />
            )}
          />
        </Box>
        <Box sx={{ ml: 2 }}>
          <Select
            label="Transfer deadline"
            id="transferDeadline"
            name="transferDeadline"
            value={
              filters.transferDeadline
                ? convertLastDayToMonth(filters.transferDeadline)
                : "all"
            }
            onChange={(e) => {
              const selectedMonth = e.target.value;
              setFilters(() => ({
                ...filters,
                transferDeadline:
                  selectedMonth === "all"
                    ? null
                    : convertMonthToLastDay(selectedMonth),
              }));
            }}
            minWidth={200}
            options={[
              { label: "All", value: "all" },
              ...generateFutureMonths(12).map((month) => ({
                label: month,
                value: month,
              })),
            ]}
          />
        </Box>
      </Box>
      <Box display="flex" justifyContent="start" alignItems="center" mt={1}>
        <Box>
          <Select
            label="Production support"
            id="productionSupport"
            name="productionSupport"
            value={filters.productionSupport || "all"}
            onChange={(e) =>
              setFilters(() => ({
                ...filters,
                productionSupport: e.target.value,
              }))
            }
            minWidth={200}
            options={[
              { label: "All", value: "all" },
              { label: "Yes", value: "true" },
              { label: "No", value: "false" },
            ]}
          />
        </Box>
        <Box sx={{ ml: 2 }}>
          <Select
            label="Investment support"
            id="investmentSupport"
            name="investmentSupport"
            value={filters.investmentSupport || "all"}
            onChange={(e) =>
              setFilters(() => ({
                ...filters,
                investmentSupport: e.target.value,
              }))
            }
            minWidth={200}
            options={[
              { label: "All", value: "all" },
              { label: "Yes", value: "true" },
              { label: "No", value: "false" },
            ]}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default Filters;
